import {
  CellTemplate,
  Compatible,
  isAlphaNumericKey,
  keyCodes,
  Uncertain,
  UncertainCompatible,
} from "@silevis/reactgrid";
import { Styles } from "./Style";
import SafeHtml from "@lhd/components/SafeHtml";
import ReactQuill from "react-quill";
import { useEffect, useRef, useState } from "react";
import ReactQuillCustomJP from "@lhd/components/ReactQuillCustomJP";
import { CellCustomType } from "../../type";
import { useRowHeight } from "@lhd/components/CloneApp/fields/TableSheet/RowHeightContext";
import { useSearchParams } from "react-router-dom";

function EditMode({
  cell,
  onCellChanged,
  getCompatibleCell,
}: {
  cell: Compatible<CellCustomType>;
  onCellChanged: (cell: Compatible<CellCustomType>, commit: boolean) => void;
  getCompatibleCell: (
    c: Uncertain<CellCustomType>,
  ) => Compatible<CellCustomType>;
}) {
  const valueSubmit = cell.valueSubmit as string;
  const quillRef = useRef<ReactQuill>();
  const initialValueRef = useRef(valueSubmit);
  const latestValueRef = useRef(valueSubmit);
  const cellRef = useRef(cell);
  const onCellChangedRef = useRef(onCellChanged);
  const getCompatibleCellRef = useRef(getCompatibleCell);
  const committedRef = useRef(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [toolbarHeight, setToolbarHeight] = useState(0);

  cellRef.current = cell;
  onCellChangedRef.current = onCellChanged;
  getCompatibleCellRef.current = getCompatibleCell;

  const commit = (source: string) => {
    if (committedRef.current) return;
    committedRef.current = true;
    onCellChangedRef.current(
      getCompatibleCellRef.current({
        ...cellRef.current,
        text: latestValueRef.current,
        valueSubmit: latestValueRef.current,
      }),
      true,
    );
  };

  // Safety net: commit khi EditMode unmount
  useEffect(() => {
    return () => commit("unmount");
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const timeout = setTimeout(() => {
      const editor = quillRef.current?.getEditor();
      if (editor) {
        editor.focus();
        const length = editor.getLength();
        editor.setSelection(length, 0);
      }
    }, 100);

    return () => {
      clearTimeout(timeout);
    };
  }, [quillRef.current?.getEditor()]);

  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;

    const toolbar = el.querySelector(".ql-toolbar") as HTMLElement;

    if (toolbar) {
      const height = toolbar.getBoundingClientRect().height;
      setToolbarHeight(height);
    }
  }, []);

  const rowHeightQlEditor = (cell?.rowHeight ?? 0) - toolbarHeight - 4;
  const rowHeightQlEditorDisplay =
    rowHeightQlEditor < 42 ? 42 : rowHeightQlEditor;

  return (
    <Styles
      onClick={(e) => e.preventDefault()}
      onPointerDown={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
      rowHeightQlEditor={rowHeightQlEditorDisplay}
    >
      {/* onBlur on wrapper div — only commit when focus leaves the editor entirely (including toolbar) */}
      <div
        onKeyDown={(e) => e.stopPropagation()}
        onKeyUp={(e) => e.stopPropagation()}
        onPaste={(e) => e.stopPropagation()}
        onCopy={(e) => e.stopPropagation()}
        onBlur={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget as Node)) {
            commit("blur");
          }
        }}
        style={{
          backgroundColor: "#fff",
          height: "auto",
        }}
        ref={wrapperRef}
      >
        <ReactQuillCustomJP
          quillRef={quillRef as React.MutableRefObject<ReactQuill | undefined>}
          defaultValue={initialValueRef.current}
          onChange={(valueSubmit) => {
            cellRef.current?.onChange?.();
            latestValueRef.current = valueSubmit;
            committedRef.current = false;
            onCellChangedRef.current(
              getCompatibleCellRef.current({
                ...cellRef.current,
                text: valueSubmit,
                valueSubmit,
              }),
              false,
            );
          }}
        />
      </div>
    </Styles>
  );
}

function RichTextCellRender({
  cell,
  isInEditMode,
  onCellChanged,
  getCompatibleCell,
}: {
  cell: Compatible<CellCustomType>;
  isInEditMode: boolean;
  onCellChanged: (cell: Compatible<CellCustomType>, commit: boolean) => void;
  getCompatibleCell: (
    c: Uncertain<CellCustomType>,
  ) => Compatible<CellCustomType>;
}) {
  const { updateRowHeight } = useRowHeight();
  const prevValueRef = useRef<string | undefined>(undefined);
  const [searchParams] = useSearchParams();
  const pageMode = searchParams.get("pageMode");
  const isViewMode = pageMode === "view";
  const valueSubmit = cell.valueSubmit as string;
  useEffect(() => {
    updateRowHeight(String(cell.clientId), cell.rowIndex);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const prev = prevValueRef.current;
    prevValueRef.current = valueSubmit;
    if (prev === undefined || prev === valueSubmit) return;
    if (isInEditMode || !cell.clientId || !cell.rowIndex) return;
    updateRowHeight(String(cell.clientId), cell.rowIndex);
  }, [valueSubmit, isInEditMode]); // eslint-disable-line react-hooks/exhaustive-deps

  if (isInEditMode && !isViewMode) {
    return (
      <EditMode
        cell={cell}
        onCellChanged={onCellChanged}
        getCompatibleCell={getCompatibleCell}
      />
    );
  }

  return (
    <Styles onKeyDown={(e) => e.stopPropagation()}>
      <SafeHtml html={valueSubmit || ""} />
    </Styles>
  );
}

export class RichTextCellTemplate implements CellTemplate<CellCustomType> {
  getCompatibleCell(
    uncertainCell: Uncertain<CellCustomType>,
  ): Compatible<CellCustomType> {
    return { value: NaN, ...(uncertainCell as CellCustomType) };
  }

  getClassName(cell: Compatible<CellCustomType>, _isInEditMode: boolean) {
    return cell.className || "";
  }

  update(
    cell: Compatible<CellCustomType>,
    cellToMerge: UncertainCompatible<CellCustomType>,
  ): Compatible<CellCustomType> {
    return this.getCompatibleCell({
      ...cell,
      text: cellToMerge.text,
      valueSubmit: cellToMerge.valueSubmit,
    });
  }

  handleKeyDown(
    cell: Compatible<CellCustomType>,
    keyCode: number,
    ctrl: boolean,
    shift: boolean,
    alt: boolean,
  ): { cell: Compatible<CellCustomType>; enableEditMode: boolean } {
    if (keyCode === keyCodes.ENTER && shift) {
      return { cell, enableEditMode: true };
    }
    const isIME = keyCode === 229 || keyCode === 299;
    if (isIME) {
      return { cell, enableEditMode: true };
    }
    if (!ctrl && !alt && isAlphaNumericKey(keyCode)) {
      return { cell, enableEditMode: true };
    }
    return {
      cell,
      enableEditMode:
        keyCode === keyCodes.POINTER || keyCode === keyCodes.ENTER,
    };
  }

  render(
    cell: Compatible<CellCustomType>,
    isInEditMode: boolean,
    onCellChanged: (cell: Compatible<CellCustomType>, commit: boolean) => void,
  ) {
    return (
      <RichTextCellRender
        cell={cell}
        isInEditMode={isInEditMode}
        onCellChanged={onCellChanged}
        getCompatibleCell={(c) => this.getCompatibleCell(c)}
      />
    );
  }
}
